import { useEffect, useMemo, useRef, useState } from 'react';
import type { ElectronTelemetrySnapshot } from '@/types/electron';

export type SystemMonitorSnapshot = {
  cpu: {
    cores: number | null;
    usagePercent: number | null;
    available: boolean;
    source: 'electron' | 'browser';
  };
  memory: {
    deviceMemoryGb: number | null;
    usedHeapMb: number | null;
    heapLimitMb: number | null;
    heapUsagePercent: number;
    systemTotalMb: number | null;
    systemUsedMb: number | null;
    systemFreeMb: number | null;
    systemUsagePercent: number | null;
    appWorkingSetMb: number | null;
    source: 'electron' | 'browser';
  };
  gpu: {
    webglRenderer: string;
    webglVendor: string;
    webgpuSupported: boolean;
    webgpuAdapterName: string | null;
    webgpuVendor: string | null;
    webgpuArchitecture: string | null;
    webgpuFeatureCount: number;
    systemModel: string | null;
    systemVendor: string | null;
    systemUsagePercent: number | null;
    vramUsedMb: number | null;
    vramTotalMb: number | null;
    temperatureC: number | null;
  };
  runtime: {
    fps: number;
    frameTimeMs: number;
    activeWindows: number;
    online: boolean;
    platform: string;
    environment: 'electron' | 'browser';
  };
  processes: {
    pid: number;
    type: string;
    cpuPercent: number | null;
    memoryWorkingSetMb: number | null;
  }[];
};

const getVisibleWindowCount = () => {
  if (typeof document === 'undefined') return 0;

  return Array.from(document.querySelectorAll<HTMLElement>('[data-window-id]')).filter((element) => {
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }).length;
};

const isElectronEnvironment = () => typeof window !== 'undefined' && Boolean(window.electronAPI?.isElectron);

const readBrowserMemory = () => {
  const nav = navigator as Navigator & { deviceMemory?: number };
  const perf = performance as Performance & {
    memory?: {
      usedJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
  };

  const usedHeapMb = perf.memory ? perf.memory.usedJSHeapSize / 1024 / 1024 : null;
  const heapLimitMb = perf.memory ? perf.memory.jsHeapSizeLimit / 1024 / 1024 : null;
  const heapUsagePercent = usedHeapMb && heapLimitMb ? Math.min(100, (usedHeapMb / heapLimitMb) * 100) : 0;

  return {
    deviceMemoryGb: typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null,
    usedHeapMb,
    heapLimitMb,
    heapUsagePercent,
  };
};

const readWebGlInfo = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl =
      (canvas.getContext('webgl') as WebGLRenderingContext | null) ||
      (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null);

    if (!gl) {
      return {
        webglRenderer: 'WebGL unavailable',
        webglVendor: 'Unknown',
      };
    }

    const ext = gl.getExtension('WEBGL_debug_renderer_info') as {
      UNMASKED_RENDERER_WEBGL?: number;
      UNMASKED_VENDOR_WEBGL?: number;
    } | null;

    return {
      webglRenderer: ext?.UNMASKED_RENDERER_WEBGL
        ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL))
        : 'Sandboxed WebGL renderer',
      webglVendor: ext?.UNMASKED_VENDOR_WEBGL
        ? String(gl.getParameter(ext.UNMASKED_VENDOR_WEBGL))
        : 'Browser GPU sandbox',
    };
  } catch {
    return {
      webglRenderer: 'Unknown renderer',
      webglVendor: 'Unknown vendor',
    };
  }
};

const readWebGpuInfo = async () => {
  try {
    const nav = navigator as Navigator & {
      gpu?: {
        requestAdapter?: () => Promise<any>;
      };
    };

    if (!nav.gpu?.requestAdapter) {
      return {
        webgpuSupported: false,
        webgpuAdapterName: null,
        webgpuVendor: null,
        webgpuArchitecture: null,
        webgpuFeatureCount: 0,
      };
    }

    const adapter = await nav.gpu.requestAdapter();
    if (!adapter) {
      return {
        webgpuSupported: false,
        webgpuAdapterName: null,
        webgpuVendor: null,
        webgpuArchitecture: null,
        webgpuFeatureCount: 0,
      };
    }

    const info = (adapter.info ?? {}) as {
      description?: string;
      device?: string;
      vendor?: string;
      architecture?: string;
    };

    return {
      webgpuSupported: true,
      webgpuAdapterName: info.description || info.device || 'Adapter detected',
      webgpuVendor: info.vendor || null,
      webgpuArchitecture: info.architecture || null,
      webgpuFeatureCount: Array.from(adapter.features || []).length,
    };
  } catch {
    return {
      webgpuSupported: false,
      webgpuAdapterName: null,
      webgpuVendor: null,
      webgpuArchitecture: null,
      webgpuFeatureCount: 0,
    };
  }
};

const getElectronTelemetry = async (): Promise<ElectronTelemetrySnapshot | null> => {
  if (!isElectronEnvironment() || !window.electronAPI?.getSystemTelemetry) {
    return null;
  }

  try {
    return await window.electronAPI.getSystemTelemetry();
  } catch {
    return null;
  }
};

export function useSystemMonitor() {
  const environment = isElectronEnvironment() ? 'electron' : 'browser';
  const [snapshot, setSnapshot] = useState<SystemMonitorSnapshot>({
    cpu: {
      cores: typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || null : null,
      usagePercent: null,
      available: false,
      source: environment,
    },
    memory: {
      deviceMemoryGb: null,
      usedHeapMb: null,
      heapLimitMb: null,
      heapUsagePercent: 0,
      systemTotalMb: null,
      systemUsedMb: null,
      systemFreeMb: null,
      systemUsagePercent: null,
      appWorkingSetMb: null,
      source: environment,
    },
    gpu: {
      webglRenderer: 'Detecting renderer…',
      webglVendor: 'Detecting vendor…',
      webgpuSupported: false,
      webgpuAdapterName: null,
      webgpuVendor: null,
      webgpuArchitecture: null,
      webgpuFeatureCount: 0,
      systemModel: null,
      systemVendor: null,
      systemUsagePercent: null,
      vramUsedMb: null,
      vramTotalMb: null,
      temperatureC: null,
    },
    runtime: {
      fps: 60,
      frameTimeMs: 16.7,
      activeWindows: 0,
      online: typeof navigator !== 'undefined' ? navigator.onLine : true,
      platform: typeof navigator !== 'undefined' ? navigator.platform || 'Unknown' : 'Unknown',
      environment,
    },
    processes: [],
  });

  const fpsRef = useRef(60);
  const frameTimeRef = useRef(16.7);

  useEffect(() => {
    let frame = 0;
    let lastFrame = performance.now();

    const tick = (now: number) => {
      const delta = Math.max(1, now - lastFrame);
      lastFrame = now;

      const fps = Math.min(120, 1000 / delta);
      fpsRef.current = fpsRef.current * 0.82 + fps * 0.18;
      frameTimeRef.current = frameTimeRef.current * 0.82 + delta * 0.18;

      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const [webGpu, webGl] = await Promise.all([readWebGpuInfo(), Promise.resolve(readWebGlInfo())]);
      if (cancelled) return;

      setSnapshot((prev) => ({
        ...prev,
        gpu: {
          ...prev.gpu,
          ...webGl,
          ...webGpu,
        },
      }));
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const update = async () => {
      const browserMemory = readBrowserMemory();
      const electronTelemetry = await getElectronTelemetry();
      if (cancelled) return;

      setSnapshot((prev) => ({
        ...prev,
        cpu: {
          cores: electronTelemetry?.cpu.cores ?? (navigator.hardwareConcurrency || null),
          usagePercent: electronTelemetry?.cpu.usagePercent ?? null,
          available: typeof electronTelemetry?.cpu.usagePercent === 'number',
          source: electronTelemetry ? 'electron' : 'browser',
        },
        memory: {
          deviceMemoryGb: browserMemory.deviceMemoryGb,
          usedHeapMb: browserMemory.usedHeapMb ? Math.round(browserMemory.usedHeapMb) : null,
          heapLimitMb: browserMemory.heapLimitMb ? Math.round(browserMemory.heapLimitMb) : null,
          heapUsagePercent: Math.round(browserMemory.heapUsagePercent),
          systemTotalMb: electronTelemetry?.memory.totalMb ?? null,
          systemUsedMb: electronTelemetry?.memory.usedMb ?? null,
          systemFreeMb: electronTelemetry?.memory.freeMb ?? null,
          systemUsagePercent: electronTelemetry?.memory.usagePercent ?? null,
          appWorkingSetMb: electronTelemetry?.memory.appWorkingSetMb ?? null,
          source: electronTelemetry ? 'electron' : 'browser',
        },
        gpu: {
          ...prev.gpu,
          systemModel: electronTelemetry?.gpu.model ?? null,
          systemVendor: electronTelemetry?.gpu.vendor ?? null,
          systemUsagePercent: electronTelemetry?.gpu.utilizationPercent ?? null,
          vramUsedMb: electronTelemetry?.gpu.memoryUsedMb ?? null,
          vramTotalMb: electronTelemetry?.gpu.memoryTotalMb ?? null,
          temperatureC: electronTelemetry?.gpu.temperatureC ?? null,
        },
        runtime: {
          fps: Math.round(fpsRef.current),
          frameTimeMs: Number(frameTimeRef.current.toFixed(1)),
          activeWindows: getVisibleWindowCount(),
          online: navigator.onLine,
          platform: electronTelemetry?.platform || navigator.platform || 'Unknown',
          environment: electronTelemetry ? 'electron' : 'browser',
        },
        processes: electronTelemetry?.processes ?? [],
      }));
    };

    const handleConnectionChange = () => {
      void update();
    };

    void update();
    const interval = window.setInterval(() => {
      void update();
    }, 1200);
    window.addEventListener('online', handleConnectionChange);
    window.addEventListener('offline', handleConnectionChange);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('online', handleConnectionChange);
      window.removeEventListener('offline', handleConnectionChange);
    };
  }, []);

  return useMemo(() => snapshot, [snapshot]);
}
